import {
  useInfiniteQuery,
  useMutation,
  useQuery
} from '@tanstack/react-query'
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult
} from '@tanstack/react-query'
import axios from 'axios'
import type {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse
} from 'axios'
import type {
  CreateCourseDraft201,
  CreateCourseDraftBody,
  GetCourseDraftById200,
  GetCourseDrafts200,
  GetCourseDraftsParams,
  PublishCourseDraft200,
  UpdateCourseDraft200,
  UpdateCourseDraftBody,
  UpdateDraftLectureContent200,
  UpdateDraftLectureContentBody,
  UpdateDraftLectureMarkdown200,
  UpdateDraftLectureMarkdown400,
  UpdateDraftLectureMarkdown401,
  UpdateDraftLectureMarkdown403,
  UpdateDraftLectureMarkdown404,
  UpdateDraftLectureMarkdownBody
} from './types'


type AwaitedInput<T> = PromiseLike<T> | T;

      type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;



/**
 * 사용자의 강의 초안 목록을 조회합니다. `filter=my_drafts` 파라미터를 사용하여 현재 로그인한 사용자의 초안만 필터링할 수 있습니다.
 * @summary 강의 초안 목록 조회
 */
export const getCourseDrafts = (
    params?: GetCourseDraftsParams, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<GetCourseDrafts200>> => {
    
    return axios.get(
      `https://api.ugot.uk/api/courses/drafts`,{
    ...options,
        params: {...params, ...options?.params},}
    );
  }


export const getGetCourseDraftsQueryKey = (params?: GetCourseDraftsParams,) => {
    return [`https://api.ugot.uk/api/courses/drafts`, ...(params ? [params]: [])] as const;
    }

    
export const getGetCourseDraftsInfiniteQueryOptions = <TData = Awaited<ReturnType<typeof getCourseDrafts>>, TError = AxiosError<void>>(params?: GetCourseDraftsParams, options?: { query?:UseInfiniteQueryOptions<Awaited<ReturnType<typeof getCourseDrafts>>, TError, TData>, axios?: AxiosRequestConfig}
) => {

const {query: queryOptions, axios: axiosOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetCourseDraftsQueryKey(params);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getCourseDrafts>>> = ({ signal }) => getCourseDrafts(params, { signal, ...axiosOptions });

      

      

   return  { queryKey, queryFn,   staleTime: 300000, gcTime: 1800000,  ...queryOptions} as UseInfiniteQueryOptions<Awaited<ReturnType<typeof getCourseDrafts>>, TError, TData> & { queryKey: QueryKey }
}

export type GetCourseDraftsInfiniteQueryResult = NonNullable<Awaited<ReturnType<typeof getCourseDrafts>>>
export type GetCourseDraftsInfiniteQueryError = AxiosError<void>

/**
 * @summary 강의 초안 목록 조회
 */
export const useGetCourseDraftsInfinite = <TData = Awaited<ReturnType<typeof getCourseDrafts>>, TError = AxiosError<void>>(
 params?: GetCourseDraftsParams, options?: { query?:UseInfiniteQueryOptions<Awaited<ReturnType<typeof getCourseDrafts>>, TError, TData>, axios?: AxiosRequestConfig}

  ):  UseInfiniteQueryResult<TData, TError> & { queryKey: QueryKey } => {

  const queryOptions = getGetCourseDraftsInfiniteQueryOptions(params,options)

  const query = useInfiniteQuery(queryOptions) as  UseInfiniteQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}



export const getGetCourseDraftsQueryOptions = <TData = Awaited<ReturnType<typeof getCourseDrafts>>, TError = AxiosError<void>>(params?: GetCourseDraftsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getCourseDrafts>>, TError, TData>, axios?: AxiosRequestConfig}
) => {

const {query: queryOptions, axios: axiosOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetCourseDraftsQueryKey(params);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getCourseDrafts>>> = ({ signal }) => getCourseDrafts(params, { signal, ...axiosOptions });

      

      

   return  { queryKey, queryFn,   staleTime: 300000, gcTime: 1800000,  ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getCourseDrafts>>, TError, TData> & { queryKey: QueryKey }
}

export type GetCourseDraftsQueryResult = NonNullable<Awaited<ReturnType<typeof getCourseDrafts>>>
export type GetCourseDraftsQueryError = AxiosError<void>

/**
 * @summary 강의 초안 목록 조회
 */
export const useGetCourseDrafts = <TData = Awaited<ReturnType<typeof getCourseDrafts>>, TError = AxiosError<void>>(
 params?: GetCourseDraftsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getCourseDrafts>>, TError, TData>, axios?: AxiosRequestConfig}

  ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } => {

  const queryOptions = getGetCourseDraftsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}



/**
 * 새로운 강의 초안을 JSON 형식으로 생성합니다. 제목, 설명, 레벨, 썸네일 URL 및 섹션 정보를 포함할 수 있습니다.
 * @summary 새 강의 초안 생성 (JSON)
 */
export const createCourseDraft = (
    createCourseDraftBody: CreateCourseDraftBody, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<CreateCourseDraft201>> => {
    
    return axios.post(
      `https://api.ugot.uk/api/courses/drafts`,
      createCourseDraftBody,options
    );
  }



export const getCreateCourseDraftMutationOptions = <TError = AxiosError<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createCourseDraft>>, TError,{data: CreateCourseDraftBody}, TContext>, axios?: AxiosRequestConfig}
): UseMutationOptions<Awaited<ReturnType<typeof createCourseDraft>>, TError,{data: CreateCourseDraftBody}, TContext> => {
const {mutation: mutationOptions, axios: axiosOptions} = options ?? {};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createCourseDraft>>, {data: CreateCourseDraftBody}> = (props) => {
          const {data} = props ?? {};

          return  createCourseDraft(data,axiosOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type CreateCourseDraftMutationResult = NonNullable<Awaited<ReturnType<typeof createCourseDraft>>>
    export type CreateCourseDraftMutationBody = CreateCourseDraftBody
    export type CreateCourseDraftMutationError = AxiosError<void>

    /**
 * @summary 새 강의 초안 생성 (JSON)
 */
export const useCreateCourseDraft = <TError = AxiosError<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createCourseDraft>>, TError,{data: CreateCourseDraftBody}, TContext>, axios?: AxiosRequestConfig}
): UseMutationResult<
        Awaited<ReturnType<typeof createCourseDraft>>,
        TError,
        {data: CreateCourseDraftBody},
        TContext
      > => {

      const mutationOptions = getCreateCourseDraftMutationOptions(options);

      return useMutation(mutationOptions);
    }
    /**
 * ID로 특정 강의 초안의 상세 정보를 조회합니다.
 * @summary 특정 강의 초안 조회
 */
export const getCourseDraftById = (
    id: number, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<GetCourseDraftById200>> => {
    
    return axios.get(
      `https://api.ugot.uk/api/courses/drafts/${id}`,options
    );
  }


export const getGetCourseDraftByIdQueryKey = (id: number,) => {
    return [`https://api.ugot.uk/api/courses/drafts/${id}`] as const;
    }

    
export const getGetCourseDraftByIdInfiniteQueryOptions = <TData = Awaited<ReturnType<typeof getCourseDraftById>>, TError = AxiosError<void>>(id: number, options?: { query?:UseInfiniteQueryOptions<Awaited<ReturnType<typeof getCourseDraftById>>, TError, TData>, axios?: AxiosRequestConfig}
) => {

const {query: queryOptions, axios: axiosOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetCourseDraftByIdQueryKey(id);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getCourseDraftById>>> = ({ signal }) => getCourseDraftById(id, { signal, ...axiosOptions });

      

      

   return  { queryKey, queryFn, enabled: !!(id),  staleTime: 300000, gcTime: 1800000,  ...queryOptions} as UseInfiniteQueryOptions<Awaited<ReturnType<typeof getCourseDraftById>>, TError, TData> & { queryKey: QueryKey }
}

export type GetCourseDraftByIdInfiniteQueryResult = NonNullable<Awaited<ReturnType<typeof getCourseDraftById>>>
export type GetCourseDraftByIdInfiniteQueryError = AxiosError<void>

/**
 * @summary 특정 강의 초안 조회
 */
export const useGetCourseDraftByIdInfinite = <TData = Awaited<ReturnType<typeof getCourseDraftById>>, TError = AxiosError<void>>(
 id: number, options?: { query?:UseInfiniteQueryOptions<Awaited<ReturnType<typeof getCourseDraftById>>, TError, TData>, axios?: AxiosRequestConfig}

  ):  UseInfiniteQueryResult<TData, TError> & { queryKey: QueryKey } => {

  const queryOptions = getGetCourseDraftByIdInfiniteQueryOptions(id,options)

  const query = useInfiniteQuery(queryOptions) as  UseInfiniteQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}



export const getGetCourseDraftByIdQueryOptions = <TData = Awaited<ReturnType<typeof getCourseDraftById>>, TError = AxiosError<void>>(id: number, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getCourseDraftById>>, TError, TData>, axios?: AxiosRequestConfig}
) => {

const {query: queryOptions, axios: axiosOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetCourseDraftByIdQueryKey(id);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getCourseDraftById>>> = ({ signal }) => getCourseDraftById(id, { signal, ...axiosOptions });

      

      

   return  { queryKey, queryFn, enabled: !!(id),  staleTime: 300000, gcTime: 1800000,  ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getCourseDraftById>>, TError, TData> & { queryKey: QueryKey }
}

export type GetCourseDraftByIdQueryResult = NonNullable<Awaited<ReturnType<typeof getCourseDraftById>>>
export type GetCourseDraftByIdQueryError = AxiosError<void>

/**
 * @summary 특정 강의 초안 조회
 */
export const useGetCourseDraftById = <TData = Awaited<ReturnType<typeof getCourseDraftById>>, TError = AxiosError<void>>(
 id: number, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getCourseDraftById>>, TError, TData>, axios?: AxiosRequestConfig}

  ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } => {

  const queryOptions = getGetCourseDraftByIdQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}



/**
 * 강의 초안의 메타데이터(제목, 설명, 레벨, 썸네일 URL 등)와 커리큘럼 구조를 JSON 형식으로 업데이트합니다.
썸네일 이미지나 강의별 마크다운 콘텐츠와 같은 파일은 각각의 전용 API를 통해 먼저 업로드해야 하며, 
이 API에서는 해당 파일들의 URL이나 참조 ID를 사용합니다.
 * @summary 강의 초안 정보 업데이트 (JSON)
 */
export const updateCourseDraft = (
    id: number,
    updateCourseDraftBody: UpdateCourseDraftBody, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<UpdateCourseDraft200>> => {
    
    return axios.put(
      `https://api.ugot.uk/api/courses/drafts/${id}`,
      updateCourseDraftBody,options
    );
  }



export const getUpdateCourseDraftMutationOptions = <TError = AxiosError<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateCourseDraft>>, TError,{id: number;data: UpdateCourseDraftBody}, TContext>, axios?: AxiosRequestConfig}
): UseMutationOptions<Awaited<ReturnType<typeof updateCourseDraft>>, TError,{id: number;data: UpdateCourseDraftBody}, TContext> => {
const {mutation: mutationOptions, axios: axiosOptions} = options ?? {};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateCourseDraft>>, {id: number;data: UpdateCourseDraftBody}> = (props) => {
          const {id,data} = props ?? {};

          return  updateCourseDraft(id,data,axiosOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type UpdateCourseDraftMutationResult = NonNullable<Awaited<ReturnType<typeof updateCourseDraft>>>
    export type UpdateCourseDraftMutationBody = UpdateCourseDraftBody
    export type UpdateCourseDraftMutationError = AxiosError<void>

    /**
 * @summary 강의 초안 정보 업데이트 (JSON)
 */
export const useUpdateCourseDraft = <TError = AxiosError<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateCourseDraft>>, TError,{id: number;data: UpdateCourseDraftBody}, TContext>, axios?: AxiosRequestConfig}
): UseMutationResult<
        Awaited<ReturnType<typeof updateCourseDraft>>,
        TError,
        {id: number;data: UpdateCourseDraftBody},
        TContext
      > => {

      const mutationOptions = getUpdateCourseDraftMutationOptions(options);

      return useMutation(mutationOptions);
    }
    /**
 * 강의 초안을 삭제합니다.
 * @summary 강의 초안 삭제
 */
export const deleteCourseDraft = (
    id: number, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<void>> => {
    
    return axios.delete(
      `https://api.ugot.uk/api/courses/drafts/${id}`,options
    );
  }



export const getDeleteCourseDraftMutationOptions = <TError = AxiosError<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteCourseDraft>>, TError,{id: number}, TContext>, axios?: AxiosRequestConfig}
): UseMutationOptions<Awaited<ReturnType<typeof deleteCourseDraft>>, TError,{id: number}, TContext> => {
const {mutation: mutationOptions, axios: axiosOptions} = options ?? {};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteCourseDraft>>, {id: number}> = (props) => {
          const {id} = props ?? {};

          return  deleteCourseDraft(id,axiosOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type DeleteCourseDraftMutationResult = NonNullable<Awaited<ReturnType<typeof deleteCourseDraft>>>
    
    export type DeleteCourseDraftMutationError = AxiosError<void>

    /**
 * @summary 강의 초안 삭제
 */
export const useDeleteCourseDraft = <TError = AxiosError<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteCourseDraft>>, TError,{id: number}, TContext>, axios?: AxiosRequestConfig}
): UseMutationResult<
        Awaited<ReturnType<typeof deleteCourseDraft>>,
        TError,
        {id: number},
        TContext
      > => {

      const mutationOptions = getDeleteCourseDraftMutationOptions(options);

      return useMutation(mutationOptions);
    }
    /**
 * 강의 초안을 정식 강의로 발행합니다.
 * @summary 강의 초안 발행
 */
export const publishCourseDraft = (
    id: number, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<PublishCourseDraft200>> => {
    
    return axios.post(
      `https://api.ugot.uk/api/courses/drafts/${id}/publish`,undefined,options
    );
  }



export const getPublishCourseDraftMutationOptions = <TError = AxiosError<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof publishCourseDraft>>, TError,{id: number}, TContext>, axios?: AxiosRequestConfig}
): UseMutationOptions<Awaited<ReturnType<typeof publishCourseDraft>>, TError,{id: number}, TContext> => {
const {mutation: mutationOptions, axios: axiosOptions} = options ?? {};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof publishCourseDraft>>, {id: number}> = (props) => {
          const {id} = props ?? {};

          return  publishCourseDraft(id,axiosOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type PublishCourseDraftMutationResult = NonNullable<Awaited<ReturnType<typeof publishCourseDraft>>>
    
    export type PublishCourseDraftMutationError = AxiosError<void>

    /**
 * @summary 강의 초안 발행
 */
export const usePublishCourseDraft = <TError = AxiosError<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof publishCourseDraft>>, TError,{id: number}, TContext>, axios?: AxiosRequestConfig}
): UseMutationResult<
        Awaited<ReturnType<typeof publishCourseDraft>>,
        TError,
        {id: number},
        TContext
      > => {

      const mutationOptions = getPublishCourseDraftMutationOptions(options);

      return useMutation(mutationOptions);
    }
    /**
 * 특정 강의 초안에 속한 개별 강의의 내용(파일 업로드를 통한 마크다운 등)을 업데이트합니다.
 * @summary 강의 초안 내 특정 강의 내용 업데이트
 */
export const updateDraftLectureContent = (
    draftId: number,
    lectureId: number,
    updateDraftLectureContentBody: UpdateDraftLectureContentBody, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<UpdateDraftLectureContent200>> => {const formData = new FormData();
if(updateDraftLectureContentBody.markdownFile !== undefined) {
 formData.append('markdownFile', updateDraftLectureContentBody.markdownFile)
 }
if(updateDraftLectureContentBody.title !== undefined) {
 formData.append('title', updateDraftLectureContentBody.title)
 }

    
    return axios.put(
      `https://api.ugot.uk/api/courses/drafts/${draftId}/lectures/${lectureId}`,
      formData,options
    );
  }



export const getUpdateDraftLectureContentMutationOptions = <TError = AxiosError<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateDraftLectureContent>>, TError,{draftId: number;lectureId: number;data: UpdateDraftLectureContentBody}, TContext>, axios?: AxiosRequestConfig}
): UseMutationOptions<Awaited<ReturnType<typeof updateDraftLectureContent>>, TError,{draftId: number;lectureId: number;data: UpdateDraftLectureContentBody}, TContext> => {
const {mutation: mutationOptions, axios: axiosOptions} = options ?? {};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateDraftLectureContent>>, {draftId: number;lectureId: number;data: UpdateDraftLectureContentBody}> = (props) => {
          const {draftId,lectureId,data} = props ?? {};

          return  updateDraftLectureContent(draftId,lectureId,data,axiosOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type UpdateDraftLectureContentMutationResult = NonNullable<Awaited<ReturnType<typeof updateDraftLectureContent>>>
    export type UpdateDraftLectureContentMutationBody = UpdateDraftLectureContentBody
    export type UpdateDraftLectureContentMutationError = AxiosError<void>

    /**
 * @summary 강의 초안 내 특정 강의 내용 업데이트
 */
export const useUpdateDraftLectureContent = <TError = AxiosError<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateDraftLectureContent>>, TError,{draftId: number;lectureId: number;data: UpdateDraftLectureContentBody}, TContext>, axios?: AxiosRequestConfig}
): UseMutationResult<
        Awaited<ReturnType<typeof updateDraftLectureContent>>,
        TError,
        {draftId: number;lectureId: number;data: UpdateDraftLectureContentBody},
        TContext
      > => {

      const mutationOptions = getUpdateDraftLectureContentMutationOptions(options);

      return useMutation(mutationOptions);
    }
    /**
 * ID로 지정된 강의 초안의 특정 강의에 대한 마크다운 파일을 업로드하여 업데이트합니다.
 * @summary 강의 초안 내 특정 강의의 마크다운 콘텐츠 업데이트
 */
export const updateDraftLectureMarkdown = (
    draftId: number,
    lectureId: number,
    updateDraftLectureMarkdownBody: UpdateDraftLectureMarkdownBody, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<UpdateDraftLectureMarkdown200>> => {const formData = new FormData();
if(updateDraftLectureMarkdownBody.markdownFile !== undefined) {
 formData.append('markdownFile', updateDraftLectureMarkdownBody.markdownFile)
 }

    
    return axios.put(
      `https://api.ugot.uk/api/courses/drafts/${draftId}/lectures/${lectureId}/markdown`,
      formData,options
    );
  }



export const getUpdateDraftLectureMarkdownMutationOptions = <TError = AxiosError<UpdateDraftLectureMarkdown400 | UpdateDraftLectureMarkdown401 | UpdateDraftLectureMarkdown403 | UpdateDraftLectureMarkdown404>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateDraftLectureMarkdown>>, TError,{draftId: number;lectureId: number;data: UpdateDraftLectureMarkdownBody}, TContext>, axios?: AxiosRequestConfig}
): UseMutationOptions<Awaited<ReturnType<typeof updateDraftLectureMarkdown>>, TError,{draftId: number;lectureId: number;data: UpdateDraftLectureMarkdownBody}, TContext> => {
const {mutation: mutationOptions, axios: axiosOptions} = options ?? {};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateDraftLectureMarkdown>>, {draftId: number;lectureId: number;data: UpdateDraftLectureMarkdownBody}> = (props) => {
          const {draftId,lectureId,data} = props ?? {};

          return  updateDraftLectureMarkdown(draftId,lectureId,data,axiosOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type UpdateDraftLectureMarkdownMutationResult = NonNullable<Awaited<ReturnType<typeof updateDraftLectureMarkdown>>>
    export type UpdateDraftLectureMarkdownMutationBody = UpdateDraftLectureMarkdownBody
    export type UpdateDraftLectureMarkdownMutationError = AxiosError<UpdateDraftLectureMarkdown400 | UpdateDraftLectureMarkdown401 | UpdateDraftLectureMarkdown403 | UpdateDraftLectureMarkdown404>

    /**
 * @summary 강의 초안 내 특정 강의의 마크다운 콘텐츠 업데이트
 */
export const useUpdateDraftLectureMarkdown = <TError = AxiosError<UpdateDraftLectureMarkdown400 | UpdateDraftLectureMarkdown401 | UpdateDraftLectureMarkdown403 | UpdateDraftLectureMarkdown404>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateDraftLectureMarkdown>>, TError,{draftId: number;lectureId: number;data: UpdateDraftLectureMarkdownBody}, TContext>, axios?: AxiosRequestConfig}
): UseMutationResult<
        Awaited<ReturnType<typeof updateDraftLectureMarkdown>>,
        TError,
        {draftId: number;lectureId: number;data: UpdateDraftLectureMarkdownBody},
        TContext
      > => {

      const mutationOptions = getUpdateDraftLectureMarkdownMutationOptions(options);

      return useMutation(mutationOptions);
    }
    